import { useSlideNavigation } from "@/hooks/useSlideNavigation";
import { useEffect, useCallback } from "react";
import { SlideNav } from "./SlideNav";
import { Slide } from "./Slide";
import { INVESTOR_SLIDES } from "@/lib/constants";
import { TitleSlide } from "./slides/TitleSlide";
import { OpportunitySlide } from "./slides/OpportunitySlide";
import { SolutionSlide } from "./slides/SolutionSlide";
import { ProofSlide } from "./slides/ProofSlide";
import { BusinessModelSlide } from "./slides/BusinessModelSlide";
import { UnitEconomicsSlide } from "./slides/UnitEconomicsSlide";
import { DualTierSlide } from "./slides/DualTierSlide";
import { MarketSlide } from "./slides/MarketSlide";
import { BiggerPictureSlide } from "./slides/BiggerPictureSlide";
import { WhyUsSlide } from "./slides/WhyUsSlide";
import { NewRoadmapSlide } from "./slides/NewRoadmapSlide";
import { TeamSlide } from "./slides/TeamSlide";
import { TheAskSlide } from "./slides/TheAskSlide";
import { ThankYouSlide } from "./slides/ThankYouSlide";

export const slideComponents = [
  TitleSlide,
  OpportunitySlide,
  SolutionSlide,
  ProofSlide,
  BusinessModelSlide,
  UnitEconomicsSlide,
  DualTierSlide,
  MarketSlide,
  BiggerPictureSlide,
  WhyUsSlide,
  NewRoadmapSlide,
  TeamSlide,
  TheAskSlide,
  ThankYouSlide,
];

export function SlideContainer({
  initialSlide = 0,
  onExit,
  slides = INVESTOR_SLIDES,
  components = slideComponents,
}: {
  initialSlide?: number;
  onExit?: (slideIndex: number) => void;
  slides?: readonly { readonly id: string; readonly label: string; readonly badge: string | undefined }[];
  components?: React.ComponentType<{ isActive: boolean }>[];
}) {
  const { currentSlide, goTo } = useSlideNavigation(slides.length);

  // Set initial slide on mount
  useEffect(() => {
    if (initialSlide > 0) goTo(initialSlide);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Lock body scroll in presentation mode
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  // ESC to exit
  const handleExit = useCallback(() => {
    onExit?.(currentSlide);
  }, [onExit, currentSlide]);

  useEffect(() => {
    if (!onExit) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleExit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onExit, handleExit]);

  return (
    <div className="relative h-screen overflow-hidden">
      {onExit && (
        <button
          onClick={handleExit}
          className="fixed top-6 right-6 z-[60] w-8 h-8 flex items-center justify-center rounded text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] transition-colors font-[family-name:var(--font-mono)] text-sm"
          aria-label="Exit presentation"
        >
          ESC
        </button>
      )}

      <div
        className="transition-transform duration-800 ease-[cubic-bezier(0.22,1,0.36,1)]"
        style={{ transform: `translateY(-${currentSlide * 100}vh)` }}
      >
        {components.map((SlideContent, i) => (
          <Slide
            key={slides[i]!.id}
            isActive={currentSlide === i}
            index={i}
            title={slides[i]!.label}
            badge={slides[i]!.badge}
            totalSlides={slides.length}
          >
            <SlideContent isActive={currentSlide === i} />
          </Slide>
        ))}
      </div>

      <SlideNav
        slides={slides}
        currentSlide={currentSlide}
        goTo={goTo}
      />
    </div>
  );
}
